import { Link } from 'react-router-dom';
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis } from 'recharts';
import { ArrowRight, CalendarCheck, CheckCircle2, ClipboardList, FileCheck2, Landmark, MessageSquareReply } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '../ui/button';
import { AiChip, EmptyState, ProgressBar, Section } from '../ds/Primitives';
import { StatusPill, statusLabel } from '../ds/StatusPill';
import { formatAUDate } from '../../lib/dateUtils';

const AU_TZ = { timeZone: 'Australia/Sydney' };
const greet = () => { const h = Number(new Date().toLocaleString('en-AU', { ...AU_TZ, hour: 'numeric', hourCycle: 'h23' })); return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening'; };
const longDate = () => new Date().toLocaleDateString('en-AU', { ...AU_TZ, weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
const shortWeek = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });

export const CommandHeader = ({ name }) => (
  <div data-testid="command-centre-header">
    <p className="text-meta font-medium text-muted-foreground">{longDate()}</p>
    <h1 className="mt-1 text-page-title" data-testid="dashboard-title">{greet()}{name ? `, ${name.split(' ')[0]}` : ''}</h1>
  </div>
);

export const KpiRow = ({ kpis }) => (
  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-8" data-testid="kpi-row">
    {kpis.map((k) => (
      <Link key={k.key} to={k.href} className="hover-lift rounded-lg border border-border bg-card p-3.5 shadow-card" data-testid={`kpi-${k.key}`}>
        <p className="line-clamp-2 min-h-[2.2em] text-meta font-medium leading-tight text-muted-foreground">{k.label}</p>
        <p className={cn('mt-1 text-2xl font-semibold tabular', k.tone === 'danger' && 'text-destructive')} data-testid={`kpi-${k.key}-value`}>{k.value}</p>
      </Link>))}
  </div>
);

export const PracticePulse = ({ pulse }) => (
  <Section title="Practice pulse" description="Live counts from tasks, compliance periods, requests, documents and engagement letters." testId="practice-pulse">
    <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
      {pulse.map((p) => (
        <Link key={p.key} to={p.href} className="group block" data-testid={`pulse-${p.key}`}>
          <div className="mb-1.5 flex items-baseline justify-between gap-2">
            <span className="text-sm group-hover:text-primary">{p.label}</span>
            <span className="text-sm font-semibold tabular" data-testid={`pulse-${p.key}-value`}>{p.value}{p.unit || ''}</span>
          </div>
          <ProgressBar value={p.num} max={p.den} label={p.den ? `${p.num} of ${p.den}${p.den_label ? ` ${p.den_label}` : ''}` : 'Nothing to measure yet'} />
        </Link>))}
    </div>
  </Section>
);

const AgendaGroup = ({ icon: Icon, title, items, render, testId }) => items.length > 0 && (
  <div data-testid={testId}>
    <p className="mb-1.5 flex items-center gap-1.5 text-meta font-medium text-muted-foreground"><Icon className="h-3.5 w-3.5" />{title} <span className="tabular">({items.length})</span></p>
    <ul className="space-y-1">{items.map(render)}</ul>
  </div>
);

export const TodayAgenda = ({ agenda }) => {
  const empty = !agenda.deadlines.length && !agenda.tasks_due.length && !agenda.lodged.length && !agenda.follow_ups.length;
  const li = 'flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted/60';
  return (
    <Section title="Today" testId="today-agenda">
      {empty ? <EmptyState title="Nothing due today" testId="today-empty">No statutory deadlines, task due dates, lodgements or client follow-ups fall on today.</EmptyState> : (
        <div className="space-y-4">
          <AgendaGroup icon={CalendarCheck} title="Deadlines" items={agenda.deadlines} testId="today-deadlines" render={(d) => (
            <li key={d.id}><Link className={li} to={d.task_id ? `/tasks?task=${d.task_id}` : `/clients/${d.client_id}`}><span className="truncate">{d.client_name}</span><span className="shrink-0 text-xs text-muted-foreground">{d.type}</span></Link></li>)} />
          <AgendaGroup icon={ClipboardList} title="Tasks due" items={agenda.tasks_due} testId="today-tasks" render={(t) => (
            <li key={t.id}><Link className={li} to={`/tasks?task=${t.id}`}><span className="truncate">{t.title}</span><StatusPill status={t.status} /></Link></li>)} />
          <AgendaGroup icon={Landmark} title="Lodged today" items={agenda.lodged} testId="today-lodged" render={(d) => (
            <li key={d.id}><Link className={li} to={`/clients/${d.client_id}`}><span className="truncate">{d.client_name}</span><span className="shrink-0 text-xs text-muted-foreground">{d.type}</span></Link></li>)} />
          <AgendaGroup icon={MessageSquareReply} title="Client follow-ups" items={agenda.follow_ups} testId="today-followups" render={(r) => (
            <li key={r.id}><Link className={li} to={`/clients/${r.client_id}`}><span className="truncate">{r.client_name}</span><span className="shrink-0 text-xs text-muted-foreground">{r.label}</span></Link></li>)} />
        </div>)}
    </Section>
  );
};

export const AttentionRequired = ({ items }) => (
  <Section title="Attention required" actions={<AiChip state="rule_based" testId="attention-rule-based" />} description="Fixed rules over live practice data; no AI involved." testId="attention-required">
    {items.length === 0 ? <EmptyState icon={CheckCircle2} title="All clear" testId="attention-empty">No rule is currently flagging anything.</EmptyState> : (
      <ul className="divide-y divide-border">
        {items.map((a) => (
          <li key={a.key} className="flex items-center justify-between gap-3 py-2.5" data-testid={`attention-${a.key}`}>
            <span className="text-sm">{a.text}</span>
            <Button asChild size="sm" variant="outline" className="h-7 shrink-0 text-xs"><Link to={a.href} data-testid={`attention-${a.key}-action`}>{a.action}<ArrowRight className="ml-1 h-3 w-3" /></Link></Button>
          </li>))}
      </ul>)}
  </Section>
);

export const UpcomingDeadlines = ({ items }) => (
  <Section title="Upcoming deadlines" description="Next 14 days, not yet lodged" actions={<Link to="/compliance-dashboard" className="text-meta font-medium text-primary hover:underline">All deadlines</Link>} testId="upcoming-deadlines">
    {items.length === 0 ? <EmptyState title="No deadlines in the next 14 days" testId="upcoming-empty" /> : (
      <ul className="divide-y divide-border">
        {items.map((d) => (
          <li key={d.id} data-testid={`upcoming-${d.id}`}>
            <Link to={d.task_id ? `/tasks?task=${d.task_id}` : `/clients/${d.client_id}`} className="flex items-center justify-between gap-3 py-2 text-sm hover:text-primary">
              <span className="min-w-0"><span className="block truncate font-medium">{d.client_name}</span><span className="text-xs text-muted-foreground">{d.type}{d.period ? ` · ${d.period}` : ''}</span></span>
              <span className="shrink-0 tabular text-sm">{formatAUDate(d.date)}</span>
            </Link>
          </li>))}
      </ul>)}
  </Section>
);

export const Insights = ({ insights }) => {
  const weeks = insights.bas_weeks.map((w) => ({ ...w, label: shortWeek(w.week_start) }));
  const docs = insights.docs_unprocessed;
  const totalTasks = insights.tasks_by_status.reduce((s, x) => s + x.count, 0);
  return (
    <Section title="Insights" description="Calculated from live data. Each figure links to its source list." testId="insights">
      <div className="grid gap-6 lg:grid-cols-3">
        <div data-testid="insight-bas-weeks">
          <p className="mb-2 text-card-title">BAS workload, next 6 weeks</p>
          <div className="h-36"><ResponsiveContainer><BarChart data={weeks}><XAxis dataKey="label" tickLine={false} axisLine={false} fontSize={11} />
            <Tooltip cursor={{ fill: 'hsl(var(--muted))' }} formatter={(v) => [`${v} BAS`, 'Due']} labelFormatter={(l) => `Week of ${l}`} />
            <Bar dataKey="count" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} /></BarChart></ResponsiveContainer></div>
          <div className="mt-1 flex flex-wrap gap-x-3 text-xs">{weeks.map((w) => <Link key={w.week_start} to={w.href} className="tabular text-primary hover:underline" data-testid={`insight-week-${w.week_start}`}>{w.label}: {w.count}</Link>)}</div>
        </div>
        <Link to={docs.href} className="block rounded-lg border border-border p-4 hover:border-primary/40" data-testid="insight-docs">
          <p className="text-card-title">Documents not yet processed</p>
          <p className="mt-2 text-3xl font-semibold tabular" data-testid="insight-docs-total">{docs.total}</p>
          <p className="mt-1 text-meta text-muted-foreground"><span className="tabular">{docs.older_than_7_days}</span> uploaded more than 7 days ago</p>
          <FileCheck2 className="mt-3 h-4 w-4 text-muted-foreground" />
        </Link>
        <div data-testid="insight-tasks-by-status">
          <p className="mb-2 text-card-title">Tasks by status</p>
          <ul className="space-y-2">{insights.tasks_by_status.map((s) => (
            <li key={s.status}><Link to={`/tasks?status=${s.status}`} className="block" data-testid={`insight-status-${s.status}`}>
              <ProgressBar value={s.count} max={totalTasks} label={`${statusLabel(s.status)}: ${s.count}`} /></Link></li>))}</ul>
        </div>
      </div>
    </Section>
  );
};
